import React, { useState } from 'react';
import { unlockSession } from '../../services/crypto';
import { recoverPasswordFromMnemonic } from '../../services/recovery';
import { useAuthStore } from '../../store/authStore';
import { CriticalPathGuard } from '../ErrorBoundary';
import { SessionKeyStore } from '../../services/keyStore';

type LoginMode = 'password' | 'recovery';

export function LoginScreen(): React.JSX.Element {
  const setStep           = useAuthStore(s => s.setStep);
  const [mode, setMode]   = useState<LoginMode>('password');
  const [pw, setPw]       = useState('');
  const [showPw, setShowPw] = useState(false);
  const [mnemonic, setMnemonic] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleLogin = async (): Promise<void> => {
    setError('');
    setLoading(true);
    try {
      await unlockSession(pw);
      SessionKeyStore.resetTimeout();
      setStep('unlocked');
    } catch {
      setError('Falsches Passwort oder Datenbank-Fehler.');
    } finally {
      setLoading(false);
    }
  };

  const handleRecovery = async (): Promise<void> => {
    setError('');
    setLoading(true);
    try {
      const recovered = await recoverPasswordFromMnemonic(mnemonic.trim());
      await unlockSession(recovered);
      setStep('unlocked');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Recovery fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
      <div className="card max-w-sm w-full space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-14 h-14 rounded-2xl bg-blue-700 flex items-center justify-center mx-auto">
            <span className="text-white text-2xl font-bold">A</span>
          </div>
          <h1 className="text-xl font-semibold text-gray-900">AMed App</h1>
          <p className="text-sm text-gray-500">Dr. med. Klaus Karrer, MSc.</p>
        </div>

        {/* Mode-Toggle */}
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          {(['password', 'recovery'] as const).map(m => (
            <button
              key={m}
              onClick={() => { setMode(m); setError(''); }}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                mode === m
                  ? 'bg-blue-700 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {m === 'password' ? '🔐 Passwort' : '🔑 Recovery'}
            </button>
          ))}
        </div>

        <CriticalPathGuard guardName="Login">
          {mode === 'password' ? (
            <div className="space-y-4">
              <div>
                <label className="label">Master-Passwort</label>
                <input
                  type={showPw ? 'text' : 'password'}
                  className="input"
                  value={pw}
                  onChange={e => { setPw(e.target.value); }}
                  onKeyDown={e => { if (e.key === 'Enter') void handleLogin(); }}
                  placeholder="Ihr Passwort"
                  autoFocus
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer mt-2">
                  <input type="checkbox" checked={showPw}
                    onChange={e => { setShowPw(e.target.checked); }}
                    className="rounded border-gray-300 text-blue-600" />
                  👁 Passwort anzeigen
                </label>
              </div>
              {error && <p className="text-sm text-red-600 bg-red-50 rounded p-2">{error}</p>}
              <button
                onClick={() => { void handleLogin(); }}
                disabled={loading || !pw}
                className="btn-primary w-full justify-center"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Schlüssel wird abgeleitet…
                  </span>
                ) : 'Entsperren'}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="label">Recovery-Wortliste (24 Wörter)</label>
                <textarea
                  className="input h-32 resize-none font-mono text-xs"
                  value={mnemonic}
                  onChange={e => { setMnemonic(e.target.value); }}
                  placeholder="Wort1 Wort2 Wort3 … (alle 24 Wörter, durch Leerzeichen getrennt)"
                  spellCheck={false}
                />
                <p className="text-xs text-gray-400 mt-1">
                  {mnemonic.trim().split(/\s+/).filter(Boolean).length} / 24 Wörter
                </p>
              </div>
              {error && <p className="text-sm text-red-600 bg-red-50 rounded p-2">{error}</p>}
              <button
                onClick={() => { void handleRecovery(); }}
                disabled={loading || mnemonic.trim().split(/\s+/).length !== 24}
                className="btn-primary w-full justify-center"
              >
                {loading ? 'Wird wiederhergestellt…' : 'Passwort wiederherstellen'}
              </button>
            </div>
          )}
        </CriticalPathGuard>

        {/* Session-Timeout-Hinweis */}
        <p className="text-xs text-center text-gray-400">
          Session wird nach 10 Minuten Inaktivität automatisch gesperrt.
        </p>
      </div>
    </div>
  );
}
